import { graphql } from 'react-apollo';

import { useQuery } from '@apollo/client';
import {
  OwnableProduct_BillingCycle as BillingCycle,
  OwnableProduct_ProductType as ProductType,
} from '__generated__/graphql-types';

import { tupleToStringKey } from 'js/lib/stringKeyTuple';

import FindOwnableProductsByLegacyProductId from 'bundles/payments-common/api/FindOwnableProductsByLegacyProductIdQuery.graphql';
import FindOwnableProductsByLegacyProductIds from 'bundles/payments-common/api/FindOwnableProductsByLegacyProductIdsQuery.graphql';
import FindOwnableProductsByUnderlyingProductItemId from 'bundles/payments-common/api/FindOwnableProductsByUnderlyingProductItemIdQuery.graphql';
import type {
  FindOwnableProductsByLegacyProductIdQuery,
  FindOwnableProductsByLegacyProductIdQueryVariables,
  InstallmentPaymentSchemeFragment,
  OwnableProductByLegacyProductIdFragment,
} from 'bundles/payments-common/api/__generated__/FindOwnableProductsByLegacyProductIdQuery';
import type {
  FindOwnableProductsByLegacyProductIdsQuery,
  FindOwnableProductsByLegacyProductIdsQueryVariables,
} from 'bundles/payments-common/api/__generated__/FindOwnableProductsByLegacyProductIdsQuery';
import type {
  FindOwnableProductsByUnderlyingProductItemIdQuery,
  FindOwnableProductsByUnderlyingProductItemIdQueryVariables,
  OwnableProductByUnderlyingProductItemIdFragment,
  OwnableProductSubscriptionPaymentSchemeFragment,
  ProductTypeFragment,
} from 'bundles/payments-common/api/__generated__/FindOwnableProductsByUnderlyingProductItemIdQuery';
import ProductSkusProductType from 'bundles/payments-common/common/ProductType';
import { subscriptionBillingType } from 'bundles/payments-common/common/constants';

// Map of ProductSkusV1 productType enum values to their OwnableProduct productType equivalent
export const OWNABLE_PRODUCT_PRODUCT_TYPE_MAP = {
  [ProductSkusProductType.CATALOG_SUBSCRIPTION]: ProductType.ProductTypeInvalid,
  [ProductSkusProductType.SPARK_COURSE_SHELL]: ProductType.ProductTypeInvalid,
  [ProductSkusProductType.SPARK_SPECIALIZATION]: ProductType.ProductTypeInvalid,
  [ProductSkusProductType.SPARK_VERIFIED_CERTIFICATE]: ProductType.ProductTypeInvalid,
  [ProductSkusProductType.SPECIALIZATION]: ProductType.ProductTypeSpecialization,
  [ProductSkusProductType.SPECIALIZATION_PREPAID]: ProductType.ProductTypeSpecializationPrepaid,
  [ProductSkusProductType.SPECIALIZATION_SUBSCRIPTION]: ProductType.ProductTypeSpecializationSubscription,
  [ProductSkusProductType.VERIFIED_CERTIFICATE]: ProductType.ProductTypeVerifiedCertificate,
  [ProductSkusProductType.ENTERPRISE_CONTRACT]: ProductType.ProductTypeEnterpriseContract,
  [ProductSkusProductType.INTEREST_DEPOSIT]: ProductType.ProductTypeInvalid,
  [ProductSkusProductType.CREDENTIAL_TRACK_SUBSCRIPTION]: ProductType.ProductTypeCredentialTrackSubscription,
  [ProductSkusProductType.CREDENTIAL_TRACK_SUBSCRIPTION_V2]: ProductType.ProductTypeCredentialTrackSubscriptionV2,
  [ProductSkusProductType.CREDENTIAL_TRACK]: ProductType.ProductTypeCredentialTrack,
  [ProductSkusProductType.COURSERA_PLUS_SUBSCRIPTION]: ProductType.ProductTypeCourseraPlusSubscription,
  [ProductSkusProductType.COURSERA_PLUS]: ProductType.ProductTypeCourseraPlus,
  [ProductSkusProductType.COURSERA_TIER_LITE]: ProductType.ProductTypeCourseraTierLite,
} as const;

// Map of OwnableProduct productType enum values to their ProductSkusV1 productType equivalent
const PRODUCT_SKUS_PRODUCT_TYPE_MAP = Object.keys(OWNABLE_PRODUCT_PRODUCT_TYPE_MAP).reduce(
  (map: Record<string, string>, key: string) => {
    const value = OWNABLE_PRODUCT_PRODUCT_TYPE_MAP[key as keyof typeof OWNABLE_PRODUCT_PRODUCT_TYPE_MAP];

    if (value !== ProductType.ProductTypeInvalid) {
      map[value] = key;
    }

    return map;
  },
  {}
);

// Map of OwnableProduct billingCycle enum values to their ProductSkusV1 billingCycle equivalent
export const PRODUCT_SKUS_BILLING_CYCLE_MAP = {
  [BillingCycle.BillingCycleInvalid]: '',
  [BillingCycle.BillingCycleMonthly]: subscriptionBillingType.MONTHLY,
  [BillingCycle.BillingCycleBiannual]: subscriptionBillingType.BIANNUAL,
  [BillingCycle.BillingCycleAnnual]: subscriptionBillingType.ANNUAL,
};

export type Price = {
  amount: number;
  currencyCode: string;
};

export type OwnableProductByUnderlyingProductItemId = {
  isActive?: boolean;
  isSubscription: boolean;
  numberOfInstallments?: number;
  productItemId?: string;
  productType?: string;
  subscriptionType?: string;
  billingCycle?: string;
  price?: Price;
  ownershipDays?: number;
};

export const getS12nSubscriptionOwnableProductFromOwnableProducts = ({
  ownableProducts,
}: {
  ownableProducts: OwnableProductByUnderlyingProductItemId[];
}): OwnableProductByUnderlyingProductItemId | undefined => {
  const ownableProduct = ownableProducts?.find(
    (product: OwnableProductByUnderlyingProductItemId) =>
      product.isSubscription && product.productType === ProductSkusProductType.SPECIALIZATION_SUBSCRIPTION
  );

  return ownableProduct;
};

const formatOwnableProductByUnderlyingProductItemId = (
  ownableProduct: OwnableProductByUnderlyingProductItemIdFragment
): OwnableProductByUnderlyingProductItemId => {
  const { legacyProductId, paymentScheme } = ownableProduct.fulfillmentConfiguration;
  const isActive = Boolean(ownableProduct?.fulfillmentConfiguration?.isActive);
  const billingCycle =
    ((paymentScheme as OwnableProductSubscriptionPaymentSchemeFragment) ?? {}).billingCycle ??
    ((paymentScheme as InstallmentPaymentSchemeFragment) ?? {}).chargeCadence;
  const numberOfInstallments = ((paymentScheme as InstallmentPaymentSchemeFragment) ?? {})?.numberOfInstallments;
  const price = ownableProduct?.price?.amount?.value
    ? {
        amount: Number(ownableProduct.price.amount.value || 0),
        currencyCode: ownableProduct.price.amount.currencyCode as string,
      }
    : undefined;

  return {
    isActive,
    isSubscription: billingCycle
      ? [BillingCycle.BillingCycleAnnual, BillingCycle.BillingCycleMonthly].includes(billingCycle)
      : false,
    numberOfInstallments,
    productItemId: legacyProductId?.productItemId,
    productType: legacyProductId?.productType ? PRODUCT_SKUS_PRODUCT_TYPE_MAP[legacyProductId?.productType] : '',
    // TODO: Consolidate subscriptionType and billingCycle into one prop
    subscriptionType: PRODUCT_SKUS_BILLING_CYCLE_MAP[billingCycle],
    billingCycle: PRODUCT_SKUS_BILLING_CYCLE_MAP[billingCycle],
    price,
    ownershipDays: ((paymentScheme as InstallmentPaymentSchemeFragment) ?? {})?.ownershipDays,
  };
};

export type PropsForFindByUnderlyingProductItemId = {
  underlyingProductItemId?: string;
  skip?: boolean;
};

export type PropsFromFindByUnderlyingProductItemId = {
  ownableProducts: OwnableProductByUnderlyingProductItemId[];
  isLoadingOwnableProducts: boolean;
};

export const useFindOwnableProductsByUnderlyingProductItemId = ({
  underlyingProductItemId,
  skip,
}: PropsForFindByUnderlyingProductItemId) => {
  const { data, loading, error } = useQuery<
    FindOwnableProductsByUnderlyingProductItemIdQuery,
    FindOwnableProductsByUnderlyingProductItemIdQueryVariables
  >(FindOwnableProductsByUnderlyingProductItemId, {
    variables: { underlyingProductItemId: underlyingProductItemId! },
    context: { clientName: 'gatewayGql' },
    skip: skip || !underlyingProductItemId,
  });

  const ownableProductsRaw = data?.OwnableProductQueries?.findByOwnableProductItemId?.filter(Boolean) ?? [];

  return {
    ownableProducts: ownableProductsRaw.map(formatOwnableProductByUnderlyingProductItemId),
    loading,
    error,
  };
};

export const withFindOwnableProductsByUnderlyingProductItemId = graphql<
  PropsForFindByUnderlyingProductItemId,
  FindOwnableProductsByUnderlyingProductItemIdQuery,
  FindOwnableProductsByUnderlyingProductItemIdQueryVariables,
  PropsFromFindByUnderlyingProductItemId
>(FindOwnableProductsByUnderlyingProductItemId, {
  skip: ({ underlyingProductItemId, skip }) => skip || !underlyingProductItemId,
  options: ({ underlyingProductItemId }) => {
    return {
      variables: { underlyingProductItemId: underlyingProductItemId! },
      context: { clientName: 'gatewayGql' },
    };
  },
  props: ({ data, ownProps }) => {
    const ownableProductsRaw = data?.OwnableProductQueries?.findByOwnableProductItemId?.filter(Boolean) ?? [];

    return {
      ...ownProps,
      ownableProducts: ownableProductsRaw.map(formatOwnableProductByUnderlyingProductItemId),
      isLoadingOwnableProducts: data?.loading ?? false,
    };
  },
});

export type OwnableProductByLegacyProductId = {
  isActive?: boolean;
  underlyingProductItemId?: string;
  underlyingProductId?: string;
  numberOfInstallments?: number;
  billingCycle?: BillingCycle;
  ownershipDays?: number;
  price?: { amount: number; currencyCode: string };
  isSubscription?: boolean;
};

type PropsForFindByLegacyProductId = {
  productItemId?: string;
  skip?: boolean;
} & Pick<ProductTypeFragment, 'productType'>;

type PropsFromFindByLegacyProductId = {
  ownableProduct: OwnableProductByLegacyProductId;
};

type PropsForFindByLegacyProductIds = {
  legacyProductIds: FindOwnableProductsByLegacyProductIdsQueryVariables['legacyProductIds'];
  skip?: boolean;
};

type PropsFromFindByLegacyProductIds = {
  ownableProducts: OwnableProductByLegacyProductId[];
};

const formatOwnableProductByLegacyProductId = (
  ownableProduct?: OwnableProductByLegacyProductIdFragment
): OwnableProductByLegacyProductId => {
  if (!ownableProduct) {
    return {};
  }

  let ownershipDays;
  let billingCycle;
  const isActive = Boolean(ownableProduct?.fulfillmentConfiguration?.isActive);
  const underlyingProductItemId = ownableProduct.ownableProduct?.productItemId;
  const underlyingProductType = ownableProduct.ownableProduct?.productType
    ? PRODUCT_SKUS_PRODUCT_TYPE_MAP[ownableProduct.ownableProduct.productType]
    : '';
  // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  const underlyingProductId = tupleToStringKey([underlyingProductType, underlyingProductItemId]);
  const numberOfInstallments = (
    ownableProduct.fulfillmentConfiguration?.paymentScheme as InstallmentPaymentSchemeFragment
  )?.numberOfInstallments;
  if (ownableProduct?.fulfillmentConfiguration?.paymentScheme.__typename === 'OwnableProduct_OneTimePaymentScheme') {
    ownershipDays = ownableProduct.fulfillmentConfiguration.paymentScheme.ownershipDays;
  } else if (
    ownableProduct.fulfillmentConfiguration.paymentScheme.__typename === 'OwnableProduct_SubscriptionPaymentScheme'
  ) {
    billingCycle = ownableProduct.fulfillmentConfiguration.paymentScheme.billingCycle;
  }
  const price = ownableProduct?.price?.amount?.value
    ? {
        amount: Number(ownableProduct.price.amount.value || 0),
        currencyCode: ownableProduct?.price?.amount?.currencyCode as string,
      }
    : undefined;

  return {
    isActive,
    underlyingProductItemId,
    underlyingProductId,
    numberOfInstallments,
    billingCycle,
    ...(ownershipDays ? { ownershipDays } : {}),
    price,
    isSubscription: billingCycle
      ? [BillingCycle.BillingCycleAnnual, BillingCycle.BillingCycleMonthly].includes(billingCycle)
      : false,
  };
};

export const useFindOwnableProductByLegacyProductId = ({
  productItemId,
  productType,
  skip,
}: PropsForFindByLegacyProductId) => {
  const { data, loading, error } = useQuery<
    FindOwnableProductsByLegacyProductIdQuery,
    FindOwnableProductsByLegacyProductIdQueryVariables
  >(FindOwnableProductsByLegacyProductId, {
    variables: { legacyProductId: { productType: productType!, productItemId: productItemId! } },
    context: { clientName: 'gatewayGql' },
    skip: skip || !productItemId || !productType || productType === ProductType.ProductTypeInvalid,
  });

  return {
    ownableProduct: formatOwnableProductByLegacyProductId(
      data?.OwnableProductQueries?.findLatestByLegacyProductId ?? undefined
    ),
    loading,
    error,
  };
};

export const withFindOwnableProductsByLegacyProductId = graphql<
  PropsForFindByLegacyProductId,
  FindOwnableProductsByLegacyProductIdQuery,
  FindOwnableProductsByLegacyProductIdQueryVariables,
  PropsFromFindByLegacyProductId
>(FindOwnableProductsByLegacyProductId, {
  skip: ({ productItemId, productType, skip }) =>
    skip || !productItemId || !productType || productType === ProductType.ProductTypeInvalid,
  options: ({ productItemId, productType }) => ({
    variables: { legacyProductId: { productType: productType!, productItemId: productItemId! } },
    context: { clientName: 'gatewayGql' },
  }),
  props: ({ data, ownProps }) => {
    return {
      ...ownProps,
      ownableProduct: formatOwnableProductByLegacyProductId(
        data?.OwnableProductQueries?.findLatestByLegacyProductId ?? undefined
      ),
    };
  },
});

const getIsLegacyProductIdsValid = (
  legacyProductIds: FindOwnableProductsByLegacyProductIdsQueryVariables['legacyProductIds']
) => {
  return Array.isArray(legacyProductIds)
    ? legacyProductIds.every((legacyProductId) => {
        return legacyProductId?.productType && legacyProductId?.productItemId;
      })
    : legacyProductIds?.productType && legacyProductIds?.productItemId;
};

export const useFindOwnableProductsByLegacyProductIds = ({
  legacyProductIds,
  skip,
}: PropsForFindByLegacyProductIds) => {
  const { data, loading, error } = useQuery<
    FindOwnableProductsByLegacyProductIdsQuery,
    FindOwnableProductsByLegacyProductIdsQueryVariables
  >(FindOwnableProductsByLegacyProductIds, {
    variables: { legacyProductIds },
    context: { clientName: 'gatewayGql' },
    skip: skip || !getIsLegacyProductIdsValid(legacyProductIds),
  });

  const ownableProductsRaw = data?.OwnableProductQueries?.findLatestByLegacyProductIds?.filter(Boolean) ?? [];

  return {
    ownableProducts: ownableProductsRaw.map(formatOwnableProductByLegacyProductId),
    loading,
    error,
  };
};

export const withFindOwnableProductsByLegacyProductIds = graphql<
  PropsForFindByLegacyProductIds,
  FindOwnableProductsByLegacyProductIdsQuery,
  FindOwnableProductsByLegacyProductIdsQueryVariables,
  PropsFromFindByLegacyProductIds
>(FindOwnableProductsByLegacyProductIds, {
  skip: ({ legacyProductIds, skip }) => {
    return skip || !getIsLegacyProductIdsValid(legacyProductIds);
  },
  options: ({ legacyProductIds }) => ({
    variables: { legacyProductIds },
    context: { clientName: 'gatewayGql' },
  }),
  props: ({ data, ownProps }) => {
    const ownableProductsRaw = data?.OwnableProductQueries?.findLatestByLegacyProductIds?.filter(Boolean) ?? [];

    return {
      ...ownProps,
      ownableProducts: ownableProductsRaw.map(formatOwnableProductByLegacyProductId),
    };
  },
});
